import React, { useEffect, useState, useContext, useRef } from "react";
import { db } from "../../../firebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";
import { AppContext } from "../../../Context/AppContext";
import "./AddResult.css";

const ResultView = () => {
    const { user } = useContext(AppContext);

    const [department, setDepartment] = useState("");
    const [program, setProgram] = useState("");
    const [session, setSession] = useState("");
    const [semester, setSemester] = useState("");
    const [rollNo, setRollNo] = useState("");
    const [semesters, setSemesters] = useState([]);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    const resultRef = useRef(null);

    const fixedStart = ["Roll #", "Name", "Father Name", "Department", "Program", "Semester", "Session"];
    const fixedEnd = ["Total", "%age", "SGPA", "CGPA", "Remarks"];

    useEffect(() => {
        const fetchSemesters = async () => {
            if (department && program && session) {
                try {
                    const sessionRef = collection(db, "results", department, program, session);
                    const semSnapshot = await getDocs(sessionRef);
                    const semList = semSnapshot.docs.map((doc) => doc.id);
                    setSemesters(semList);
                } catch (error) {
                    console.error("Error fetching semesters:", error);
                }
            }
        };
        fetchSemesters();
    }, [department, program, session]);

    const fetchResults = async () => {
        const dept = user.department;
        const prog = user.program;
        const regNo = user.stdRegNumber;

        if (!dept || !prog || !session || !regNo) {
            setMessage({ type: "error", text: "Please select session first." });
            return;
        }

        setLoading(true);
        setMessage("");
        setResults([]);

        try {
            let allResults = [];

            // Loop fixed semesters 1 → 8
            for (let sem = 1; sem <= 8; sem++) {
                const semColRef = collection(db, "results", dept, prog, session, sem.toString());
                const q = query(semColRef, where("Roll #", "==", regNo));
                const snapshot = await getDocs(q);

                if (!snapshot.empty) {
                    snapshot.forEach((doc) => {
                        allResults.push({ ...doc.data(), semester: sem.toString() });
                    });
                }
            }

            if (allResults.length > 0) {
                setResults(allResults);
            } else {
                setMessage({ type: "error", text: "No results found for this Roll #." });
            }
        } catch (error) {
            console.error("Error fetching results:", error);
            setMessage({ type: "error", text: "Failed to fetch results." });
        }

        setLoading(false);
    };



    const getOrderedFields = (row) => {
        if (!row) return [];

        const allKeys = Object.keys(row);

        const subjectKeys = allKeys.filter((key) => {
            const lowerKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");
            return (
                !fixedStart.includes(key) &&
                !fixedEnd.includes(key) &&
                key.toLowerCase() !== "semester" &&
                !lowerKey.startsWith("srno") &&
                !lowerKey.startsWith("sr")
            );
        });

        subjectKeys.sort((a, b) => a.localeCompare(b));

        return [...fixedStart, ...subjectKeys, ...fixedEnd].filter((f) => allKeys.includes(f));
    };

    const handlePrint = () => {
        const printContents = resultRef.current.innerHTML;
        const win = window.open("", "_blank");
        win.document.write(`
      <html>
        <head>
          <title>Result Card</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            th, td { border: 1px solid #333; padding: 8px; text-align: center; }
            .studentInfo { margin-bottom: 15px; }
            .disclaimer { font-size: 12px; margin-top: 20px; color: red; }
          </style>
        </head>
        <body>
          ${printContents}
        </body>
      </html>
    `);
        win.document.close();
        win.print();
    };

    return (
        <div className="container">
            <h2 className="title">Search Student Result</h2>

            <div className="filters">
                <select className="select" value={session} onChange={(e) => setSession(e.target.value)}>
                    <option value="">Select Session</option>
                    <option value="Fall 24">Fall 24</option>
                    <option value="Spring 25">Spring 25</option>
                    <option value="Fall 25">Fall 25</option>
                </select>
                <button className="uploadBtn" onClick={fetchResults} disabled={loading}>
                    {loading ? "Searching..." : "Search Result"}
                </button>
            </div>



            {message && (
                <div className={`message ${message.type === "error" ? "error" : "success"}`}>
                    {message.text}
                </div>
            )}

            {results.length > 0 && (
                <div ref={resultRef}>
                    {results.map((res, idx) => (
                        <div className="resultCard" key={idx}>
                            {/* Header */}
                            <div className="headerr">
                                <h2>National University of Modern Languages</h2>
                                <h3>Semester Result - {session}</h3>
                            </div>

                            {/* Student Info */}
                            <div className="studentInfo">
                                <p><strong>Name:</strong> {user.firstName + " " + user.lastName}</p>
                                <p><strong>Father Name:</strong> {user.fatherName}</p>
                                <p><strong>Roll #:</strong> {res["Roll #"]}</p>
                                <p><strong>Department:</strong> {user.department}</p>
                                <p><strong>Program:</strong> {user.program}</p>
                                <p><strong>Semester:</strong> {res.semester}</p>
                            </div>

                            {/* Table */}
                            <div className="tbl">
                                <table>
                                    <thead>
                                    <tr>
                                        <th>Subject</th>
                                        <th>Marks</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {getOrderedFields(res)
                                        .filter((f) => !fixedStart.includes(f) && !fixedEnd.includes(f))
                                        .map((subj, sIdx) => (
                                            <tr key={sIdx}>
                                                <td>{subj}</td>
                                                <td>{res[subj]}</td>
                                            </tr>
                                        ))}
                                    <tr><td><strong>Total</strong></td><td>{res["Total"]}</td></tr>
                                    <tr><td><strong>%age</strong></td><td>{res["%age"]}</td></tr>
                                    <tr><td><strong>SGPA</strong></td><td>{res["SGPA"]}</td></tr>
                                    <tr><td><strong>CGPA</strong></td><td>{res["CGPA"]}</td></tr>
                                    <tr><td><strong>Remarks</strong></td><td>{res["Remarks"]}</td></tr>
                                    </tbody>
                                </table>

                                {/* Disclaimer */}
                                <p className="disclaimer">
                                    Disclaimer: This result has been downloaded from portal-numl.web.app.
                                    This is not an official result card.
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {results.length > 0 && (
                <button className="printBtn" onClick={handlePrint}>
                    Print Result Card
                </button>
            )}
        </div>
    );

};

export default ResultView;
